/**
 * Route tree.
 *
 * Defined in code rather than generated from the filesystem. The tree is small and fixed by the
 * build specification, and a code-based tree keeps the route guards visible next to the routes
 * they guard instead of in a separate generated file.
 *
 * The `/*` subtree is gated once, in its layout route's `beforeLoad`. Putting the check there
 * rather than in each screen means a new route added later is protected by default -- the failure
 * mode of forgetting is a redirect to login, not an unguarded page.
 *
 * Route protection here is a user-experience concern only. It decides what to render, never what
 * is permitted: the Platform API validates the session on every request and would reject anything
 * this guard let through.
 */
import type { JSX } from 'react';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { apiRequest } from '../lib/api-client';
import { queryKeys, type SessionResponse } from '../lib/api';
import { RootLayout } from './root-layout';
import { AppLayout } from './app-layout';
import { LoginRoute } from './auth/login';
import { RegisterRoute } from './auth/register';
import { VerifyEmailRoute } from './auth/verify-email';
import { ForgotPasswordRoute } from './auth/forgot-password';
import { ResetPasswordRoute } from './auth/reset-password';
import { OverviewRoute } from './app/overview';
import { ApiKeysRoute } from './app/api-keys';
import { WorkspaceRoute } from './app/workspace';
import { SettingsRoute } from './app/settings';
import { PlaygroundRoute } from './app/playground';
import { RequestLogsRoute } from './app/request-logs';
import { RequestLogDetailRoute } from './app/request-log-detail';
import { BillingRoute } from './app/billing';
import { PayRoute } from './pay';

export interface RouterContext {
  queryClient: QueryClient;
}

/**
 * Resolve the session, preferring anything already cached.
 *
 * Reusing the cache matters for navigation: without it, every move between `/*` routes would
 * re-run the guard and issue another `/session` request.
 */
async function resolveSession(queryClient: QueryClient): Promise<SessionResponse | null> {
  return queryClient.fetchQuery({
    queryKey: queryKeys.session,
    queryFn: async () => {
      try {
        return await apiRequest<SessionResponse>('/session');
      } catch {
        return null;
      }
    },
    staleTime: 30_000,
  });
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async ({ context }) => {
    const session = await resolveSession(context.queryClient);
    // TanStack Router signals a redirect by throwing its own control-flow object, not an Error.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: session === null ? '/login' : '/overview' });
  },
});

/**
 * Authentication routes.
 *
 * An already-authenticated visitor is sent to the application rather than shown a login form,
 * which is what makes a bookmarked `/login` behave sensibly.
 */
function authRoute<const P extends string>(path: P, component: () => JSX.Element) {
  return createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: async ({ context }) => {
      const session = await resolveSession(context.queryClient);
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      if (session !== null) throw redirect({ to: '/overview' });
    },
    component,
  });
}

/**
 * Login declares its own search schema because the `/*` guard redirects here with the
 * destination it interrupted. Declaring it makes the parameter type-safe rather than something
 * the screen has to assert about.
 */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // `oauth_error` is how the Platform API reports a Google sign-in that did not complete: the
  // browser arrives here from a redirect, with nothing else to carry the reason.
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; oauth_error?: string } => ({
    ...(typeof search.redirect === 'string' ? { redirect: search.redirect } : {}),
    ...(typeof search.oauth_error === 'string' ? { oauth_error: search.oauth_error } : {}),
  }),
  beforeLoad: async ({ context }) => {
    const session = await resolveSession(context.queryClient);
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    if (session !== null) throw redirect({ to: '/overview' });
  },
  component: LoginRoute,
});
const registerRoute = authRoute('/register', RegisterRoute);
const forgotPasswordRoute = authRoute('/forgot-password', ForgotPasswordRoute);

/**
 * Verification and reset consume a token from the query string, so they must render for a
 * logged-in visitor too -- someone can follow a verification link while already signed in.
 */
const verifyEmailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/verify-email',
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    ...(typeof search.token === 'string' ? { token: search.token } : {}),
  }),
  component: VerifyEmailRoute,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  validateSearch: (search: Record<string, unknown>): { token?: string } => ({
    ...(typeof search.token === 'string' ? { token: search.token } : {}),
  }),
  component: ResetPasswordRoute,
});

/**
 * Paddle's payment link, outside the protected subtree.
 *
 * Customers reach it from Paddle's emails, often signed out, and the login redirect would drop the
 * `?_ptxn=` it exists to read. It needs no session: see `pay.tsx`.
 */
const payRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/pay',
  validateSearch: (search: Record<string, unknown>): { _ptxn?: string } => ({
    ...(typeof search._ptxn === 'string' ? { _ptxn: search._ptxn } : {}),
  }),
  component: PayRoute,
});

/** The protected subtree. One guard covers every screen beneath it. */
const appLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'app',
  beforeLoad: async ({ context, location }) => {
    const session = await resolveSession(context.queryClient);
    if (session === null) {
      // The intended destination is carried so login can return the customer to it rather than
      // dropping them on the overview page.
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/login', search: { redirect: location.href } });
    }
    return { session };
  },
  component: AppLayout,
});

function appRoute<const P extends string>(path: P, component: () => JSX.Element) {
  return createRoute({ getParentRoute: () => appLayoutRoute, path, component });
}

const overviewRoute = appRoute('/overview', OverviewRoute);
const apiKeysRoute = appRoute('/api-keys', ApiKeysRoute);
const workspaceRoute = appRoute('/workspace', WorkspaceRoute);
/** Connecting Google from Settings reports a failure back here, as login does. */
const settingsRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/settings',
  validateSearch: (search: Record<string, unknown>): { oauth_error?: string } => ({
    ...(typeof search.oauth_error === 'string' ? { oauth_error: search.oauth_error } : {}),
  }),
  component: SettingsRoute,
});
const playgroundRoute = appRoute('/playground', PlaygroundRoute);
const billingRoute = appRoute('/billing', BillingRoute);
const requestLogsRoute = appRoute('/request-logs', RequestLogsRoute);

const requestLogDetailRoute = createRoute({
  getParentRoute: () => appLayoutRoute,
  path: '/request-logs/$requestId',
  component: RequestLogDetailRoute,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  verifyEmailRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  payRoute,
  appLayoutRoute.addChildren([
    overviewRoute,
    apiKeysRoute,
    workspaceRoute,
    settingsRoute,
    playgroundRoute,
    billingRoute,
    requestLogsRoute,
    requestLogDetailRoute,
  ]),
]);

export function buildRouter(queryClient: QueryClient) {
  return createRouter({
    routeTree,
    context: { queryClient },
    defaultPreload: 'intent',
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof buildRouter>;
  }
}
